// src/pages/RedirectReviewInbox.jsx
// RedirectReviewInbox — ambiguous scraper redirects awaiting adopt/retire.
//
// A case appears when a scraper target's URL redirects to a page that still
// shows a price, but the destination can't be proven to be the SAME product
// (host/locale/path mismatch) — see docs/features/scraper.md §Redirect
// review queue. Backend: /api/scrapers/redirect-reviews
// (backend/scrapers/routes.py §REDIRECT REVIEW QUEUE, migration 184).
//
// UI2 Teil 10, G6a (2026-10-20): the page is a full list surface — the list
// anatomy of `tabellen-filter-suche.md` §2.1 with the house building blocks
// (`ListToolbar`, `ResultCount`, `UnderlineTabs`, `MobileRecordCard`,
// `EmptyState`, `RowActionsMenu`) instead of a hand-rolled header + panel +
// cards. The query (tab + search) lives in the URL through `useListQuery`
// (N10.1); nothing is kept in `localStorage` (OE-6 A).
//
// 🔴 The three write paths (adopt / retire / dismiss) are UNCHANGED — same
// endpoints, same handlers, same busy gate. G6a changes the arrangement, not
// the behaviour of a decision.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Link,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import PageContainer from '../components/common/PageContainer';
import EmptyState from '../components/common/EmptyState';
import Panel from '../components/common/Panel';
import ViewHeader from '../components/ViewHeader';
import UnderlineTabs from '../components/common/UnderlineTabs';
import ListToolbar from '../components/common/ListToolbar';
import MobileRecordCard from '../components/common/MobileRecordCard';
import RowActionsMenu from '../components/common/RowActionsMenu';
import useListQuery from '../components/common/listQuery/useListQuery';
import ConfidenceBadge from '../rma/components/ConfidenceBadge';
import {
  listRedirectReviews,
  adoptRedirectReview,
  retireRedirectReview,
  dismissRedirectReview,
} from '../api/redirectReviews';
import { formatMoney } from '../utils/format';
import { SP } from '../theme/tokens';

const STATUSES = ['pending', 'adopted', 'retired', 'dismissed'];

const STATUS_LABEL = {
  pending: 'Pending',
  adopted: 'Adopted',
  retired: 'Retired',
  dismissed: 'Dismissed',
};

/** Reiter-Index ↔ stabiler Schlüssel (§10.1: the URL carries the key, never an index). */
const TAB_INDEX = STATUSES.reduce((acc, s, i) => ({ ...acc, [s]: i }), {});

/** Prefix of the tab/panel ids (`UnderlineTabs` `withPanel`). */
const PANEL_ID = 'redir';

/**
 * Phase-B forward-compat (deliberate): until Phase B fills llm_verdict, every
 * case renders the default "Unclear" chip; confidence and rationale stay
 * hidden while NULL. No rework needed when Phase B ships.
 */
const VERDICT_CHIP = {
  same_product: { label: 'Same product', color: 'success' },
  successor_model: { label: 'Successor model', color: 'warning' },
  different_product: { label: 'Different product', color: 'error' },
};

const ACTIONS = {
  adopt: adoptRedirectReview,
  retire: retireRedirectReview,
  dismiss: dismissRedirectReview,
};

/**
 * Client-side search (§4.3 — the endpoint returns ALL rows for the tab, no
 * limit, `backend/scrapers/routes.py:2907-2912`, so the loaded set IS the
 * whole stock and a client search over it is complete).
 *
 * Searched fields are exactly the six the plan names (§2.5): competitor,
 * SKU, both URLs, our product name and the found title.
 */
const SUCHFELDER = [
  'competitor_name',
  'sku',
  'old_url',
  'destination_url',
  'our_product_name',
  'found_title',
];

function passtZurSuche(c, begriff) {
  if (!begriff) return true;
  const nadel = begriff.trim().toLowerCase();
  if (!nadel) return true;
  return SUCHFELDER.some((feld) => String(c[feld] ?? '').toLowerCase().includes(nadel));
}

/** The card's state marker: the verdict chip carries the text (P22 — never colour alone). */
function Karte({ c, gesperrt, onAct }) {
  const verdictChip = VERDICT_CHIP[c.llm_verdict] || { label: 'Unclear', color: 'default' };
  // R4-W7 review correction: the case's own last_known_price freezes at
  // creation — prefer the live current_last_known_price when the backend
  // supplied one, fall back to the frozen value otherwise.
  const lastKnownPrice = c.current_last_known_price ?? c.last_known_price;

  return (
    <MobileRecordCard
      title={
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {c.competitor_name}
        </Typography>
      }
      // Die Kennung ist der fachliche Schlüssel (SKU) — nie gekürzt (N11.5).
      id={c.sku}
      // 🔴 N11.2 / R16-B1: der STATUS (Urteil + Confidence + Locale) steht vor
      // den Kerndaten. Das JSDoc dieses PRs und die Registry sagen es für genau
      // diese Ansicht zu; ohne das Prop rendert die Karte ihre Vorgabe
      // `'meta-first'` und der Chip, der die Entscheidung trägt, rutscht unter
      // das Preispaar. Hausform: `RMAInbox.jsx:1792`.
      slotOrder="chips-first"
      fieldTestIds={{
        title: `karte-titel-${c.id}`,
        id: `karte-kennung-${c.id}`,
        meta: `karte-meta-${c.id}`,
      }}
      chips={
        <>
          <Chip
            size="small"
            label={verdictChip.label}
            color={verdictChip.color}
            data-testid={`karte-status-${c.id}`}
          />
          <ConfidenceBadge confidence={c.llm_confidence} intent={c.llm_verdict} />
          {c.locale_changed && <Chip size="small" color="error" label="Locale change" />}
        </>
      }
      meta={
        <Typography variant="body2" color="text.secondary">
          {c.our_product_name || '–'} (last {formatMoney(lastKnownPrice)})
          {' → '}
          {c.found_title || '–'} ({formatMoney(c.found_price)})
        </Typography>
      }
      // 🔴 The expanded slot is a NODE, not a toggle (plan §2.5): both URLs and
      // the AI rationale are always open. `MobileRecordCard` holds no state for
      // it — the caller would, and here there is nothing to collapse.
      expanded={
        <Stack spacing={SP.chip}>
          <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
            Old:{' '}
            <Link href={c.old_url} target="_blank" rel="noreferrer">
              {c.old_url}
            </Link>
          </Typography>
          <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
            New:{' '}
            <Link href={c.destination_url} target="_blank" rel="noreferrer">
              {c.destination_url}
            </Link>
          </Typography>
          {c.llm_rationale && (
            <Typography variant="caption" color="text.secondary">
              AI: {c.llm_rationale}
            </Typography>
          )}
        </Stack>
      }
      // 🔴 §2.2 Punkt 10: ONE visible action + ⋮, and the ⋮ names its row
      // ("Actions for …"). `onPointerDown` stopPropagation is the house guard
      // against the N2 trap (`pattern-library.md:478`).
      actions={
        c.status === 'pending' ? (
          <Stack
            direction="row"
            spacing={SP.chip}
            alignItems="center"
            onPointerDown={(e) => e.stopPropagation()}
          >
            <Button
              size="small"
              variant="contained"
              disabled={gesperrt}
              onClick={() => onAct(c.id, 'adopt')}
            >
              Adopt URL
            </Button>
            <RowActionsMenu
              label={`Actions for ${c.competitor_name} ${c.sku}`}
              disabled={gesperrt}
              actions={[
                {
                  key: 'retire',
                  label: 'Retire target',
                  danger: true,
                  disabled: gesperrt,
                  onClick: () => onAct(c.id, 'retire'),
                },
                {
                  key: 'dismiss',
                  label: 'Later',
                  disabled: gesperrt,
                  onClick: () => onAct(c.id, 'dismiss'),
                },
              ]}
            />
          </Stack>
        ) : null
      }
    />
  );
}

export default function RedirectReviewInbox() {
  // 🔴 EIN Schreiber je Abfrage (§3.2/N10.1): `useListQuery` writes `tab` and
  // `q`; the page keeps no second copy. `q` is a free-text dimension, so it
  // becomes the Z3 chip "Search: …" (P34/P6) — declared, not re-derived.
  const query = useListQuery({
    tabs: { default: 'pending', values: STATUSES },
    dimensions: [{ key: 'q', kind: 'single', label: 'Search' }],
  });
  const { state, set } = query;
  const tab = STATUSES.includes(state.tab) ? state.tab : 'pending';
  const suche = state.q ?? '';

  const [data, setData] = useState(null);
  // 🔴 R15-B1 / R16-B2: die Antwort merkt sich ihren REITER. Ohne diese Zeile
  // stand nach einem gescheiterten Reiterwechsel die Zeilenmenge des ALTEN
  // Reiters unter dem neuen — samt „2 of 1 redirect reviews". Karten und `n`
  // gelten nur, wenn `dataTab === tab`; die Zählwerte sind davon unabhängig
  // (der Endpunkt liefert immer den vollen Durchbruch, `routes.py:2886-2895`).
  const [dataTab, setDataTab] = useState(null);
  const [error, setError] = useState(null);
  // 🔴 R16-B2: der AKTIONSFEHLER ist ein eigener Zustand. Er hing bis hierher
  // am selben `error` und zeigte deshalb „The list could not be reloaded", obwohl
  // nichts nachgeladen wurde — und die Ursache des Servers wurde nirgends
  // gerendert. Er sperrt jetzt auch nicht mehr ALLE Karten (nur die laufende
  // Aktion ist über `busyId` gesperrt).
  const [aktionsFehler, setAktionsFehler] = useState(null);
  // 🔴 `jeGeladen` is set in the SUCCESS branch only — never in `finally`
  // (§2.2 Punkt 7). It decides whether a failure LOCKS the toolbar (first
  // build) or merely reports above the rows (a failed refresh).
  const [jeGeladen, setJeGeladen] = useState(false);
  const [busyId, setBusyId] = useState(null);

  // 🔴 R15-W3 (Runde 2): Generationszähler gegen Antworten in falscher
  // Reihenfolge. Wechselt der Nutzer schnell (oder ist der erste Abruf langsam),
  // traf die Antwort des ALTEN Reiters nach der des neuen ein, setzte `dataTab`
  // zurück und liess `dataTab !== tab` dauerhaft wahr werden — das Skelett stand
  // dann ohne Nachladen. Nur die jeweils jüngste Anfrage darf den Zustand
  // schreiben; ältere Antworten und Fehler werden verworfen.
  const req = useRef(0);
  // 🔴 R15-W1 (Runde 3): der AKTUELLE Reiter als Ref. Ein Nachladen aus einer
  // laufenden Aktion trägt den Reiter des Renderns, in dem geklickt wurde.
  // Wechselt der Nutzer währenddessen den Reiter, ist diese Antwort die JÜNGSTE
  // und gewinnt den Generationszähler — sie setzte `dataTab` dann auf den ALTEN
  // Reiter, `dataTab !== tab` blieb wahr und das Skelett stand ohne Nachladen
  // (dieselbe Klasse wie W3, über die Aktions-Tür). Dieselbe Ref hält den
  // Aktionsfehler davon ab, unter dem NEUEN Reiter zu erscheinen.
  const tabRef = useRef(tab);
  tabRef.current = tab;

  const load = useCallback(() => {
    const id = ++req.current;
    setError(null);
    // Ein Reiterwechsel oder eine neue Abfrage macht die Meldung einer alten
    // Aktion gegenstandslos — sie verschwindet mit ihr.
    setAktionsFehler(null);
    listRedirectReviews(tab)
      .then((d) => {
        if (id !== req.current) return;
        // 🔴 R15-W1: eine Antwort für einen Reiter, der nicht mehr der aktuelle
        // ist, darf nichts setzen — sonst steht sie als Stand des falschen
        // Reiters da. Zweiter Gurt neben der Prüfung in `act`.
        if (tab !== tabRef.current) return;
        setData(d);
        setDataTab(tab);
        setJeGeladen(true);
      })
      .catch((e) => {
        if (id !== req.current) return;
        if (tab !== tabRef.current) return;
        setError({
          message:
            e?.response?.data?.error ||
            'Redirect reviews could not be loaded. Please try again.',
          detail: e?.message || String(e),
        });
      });
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (id, action) => {
    // 🔴 R15-W1 (Runde 3): der Reiter beim Aktionsstart. Wechselt der Nutzer
    // während der Aktion, gehört das Ergebnis zu einem Reiter, der nicht mehr
    // gilt: dann wird weder nachgeladen (der Abruf trüge den alten Reiter,
    // bekäme die jüngste Generation und überschriebe die Antwort des neuen)
    // noch ein Aktionsfehler gesetzt (er stünde unter dem falschen Reiter).
    const startTab = tab;
    setBusyId(id);
    setAktionsFehler(null);
    try {
      await ACTIONS[action](id);
      // §2.2 Punkt 5: list AND counters are pulled again — the counts ride in
      // the SAME response (`counts` is always the full breakdown).
      if (startTab === tabRef.current) load();
    } catch (e) {
      // 🔴 R16-B2: eigener Zustand — die Ursache des Servers wird gerendert
      // (`e?.response?.data?.error`), und der Satz behauptet NICHT, die Liste
      // sei nicht nachgeladen worden.
      if (startTab === tabRef.current) {
        setAktionsFehler({
          message:
            e?.response?.data?.error ||
            'The action could not be completed. Please try again.',
          detail: e?.message || String(e),
        });
      }
    } finally {
      setBusyId(null);
    }
  };

  // 🔴 R15-B1: die Zählwerte sind der volle Durchbruch und damit vom Filter
  // unabhängig — sie dürfen aus der letzten erfolgreichen Antwort stammen.
  // Karten und `n` dagegen gehören zu EINEM Reiter (`dataTab`).
  const counts = data?.counts ?? null;
  const gesamt = counts
    ? STATUSES.reduce((sum, s) => sum + (counts[s] ?? 0), 0)
    : null;
  const eigeneDaten = dataTab === tab ? data : null;
  const karten = useMemo(
    () => (eigeneDaten?.items ?? []).filter((c) => passtZurSuche(c, suche)),
    [eigeneDaten, suche],
  );

  // 🔴 §2.2 Punkt 7: the FIRST-build failure locks the search and names the
  // reason visibly above the toolbar. A later failed refresh keeps the rows
  // (and disables their actions — W5: no live buttons under an error banner).
  // 🔴 R15-B1/R16-B2: a failed TAB SWITCH is not a refresh — it leaves no own
  // rows (`dataTab !== tab`), so the error empty state speaks instead of the
  // previous tab's cards.
  const ersteAufbauGesperrt = Boolean(error) && !jeGeladen;
  const aktionenGesperrt = Boolean(error);
  const refetchFehler = Boolean(error) && jeGeladen && karten.length > 0;
  // RMAInbox-Hausform: der Fehler spricht, sobald keine EIGENEN Zeilen stehen.
  const fehlerOhneZeilen = Boolean(error) && karten.length === 0;

  // §2.2 Punkt 6 — the three empty states, derived, never guessed:
  //   error → the request failed (handled below), no-data → nothing exists at
  //   all, no-match → the tab/search narrows an existing stock to zero.
  // 🔴 "unknown is never 0": without `counts` the page cannot tell whether the
  // other tabs hold cases, so it says so instead of claiming "no data".
  let leerGrund = null;
  if (!error && eigeneDaten && karten.length === 0) {
    if (gesamt === 0) leerGrund = 'no-data';
    else if (gesamt === null) leerGrund = 'unknown';
    else leerGrund = 'no-match';
  }

  const zaehler = {
    noun: 'redirect reviews',
    // `n` = the filtered cards OF THIS TAB, `m` = the tab's stock (`counts[tab]`).
    n: jeGeladen && dataTab === tab ? karten.length : null,
    m: counts ? (counts[tab] ?? null) : null,
  };

  return (
    // 🔴 R16-W1 (Orchestrator-Entscheid 2026-10-21): `wide` statt `full`. A5 ist
    // eine reine Kartenliste ohne Tabelle — auf `full` steht bei 1920/2560 die
    // Aktion 2 400 px neben ihrem Datensatz. `wide` = 1600 (`tokens.js:2276`);
    // der Kopf trägt denselben Deckel. Die Frage stellt sich bei A6 erneut.
    // 🔴 R16-B3: ZWEI Flex-Kinder (`liste-kopf`, `liste-reiter`) in einem Kasten
    // mit `gap: SP.section` (24 px) — das Panel ist die dritte Zone, aber KEIN
    // Kind dieses Kastens: es steht innerhalb von `UnderlineTabs` (der `tabpanel`
    // trägt keinen Abstand, `UnderlineTabs.jsx:627-635`). Deshalb trägt das
    // Panel seinen Abstand selbst (`sx={{ mt: SP.section }}` am `Panel` in
    // Zone 3; `Panel.jsx:104-105` wendet das `sx` des Aufrufers ZULETZT an). Hausform
    // `RMAInbox.jsx:3194-3210` zieht das Panel als Geschwister heraus und
    // bekommt den `gap`; ein zusätzliches `mt` dort verdoppelte ihn.
    <PageContainer
      width="wide"
      sx={{ pb: SP.xl }}
      boxSx={{ display: 'flex', flexDirection: 'column', gap: SP.section }}
    >
      {/* Zonen-Marke `liste-kopf` VON HAND (G6a §5.3): die Marken kommen NICHT
          mit den Bausteinen (Hausform `POInbox.jsx`, `ListenMuster.jsx`). */}
      <Box data-testid="liste-kopf">
        <ViewHeader
          title="Redirect Reviews"
          subtitle="Redirects with a price at the target that a human must confirm"
          backLink={{ label: 'Scraper', href: '/scrapers' }}
          breadcrumbs={[
            { label: 'Home', href: '/' },
            { label: 'Scraper', href: '/scrapers' },
            { label: 'Redirect Reviews' },
          ]}
        />
      </Box>

      {/* Zone 3 — Reiter ÜBER dem Daten-Panel, jeder mit seiner Ziffer. */}
      <Box data-testid="liste-reiter">
        <UnderlineTabs
          // 🔴 §2.5: `scrollable` is set explicitly (the component's default is
          // "coarse pointer only", `UnderlineTabs.jsx:189`) so the four tabs
          // never clip at a narrow FINE pointer either.
          scrollable
          // 🔴 P30: panel semantics — each tab gets an id, the selected one an
          // `aria-controls`, and `children` become the `role="tabpanel"`.
          // `withPanel` does NOT name the tablist; `ariaLabel` does.
          withPanel
          ariaLabel="Review states"
          panelIdPrefix={PANEL_ID}
          tabs={STATUSES.map((s) => ({ label: STATUS_LABEL[s] }))}
          // 🔴 P11: the ALIGNED form — every tab carries its digit and the pill
          // reserves the widest count's width, so the row does not jump.
          // `null` = "not measured" (the counts did not arrive): a tab without a
          // digit, NEVER a 0 (Regel 22 Nr. 3).
          counts={STATUSES.map((s) => (counts ? counts[s] ?? null : null))}
          // `value` is an INDEX (the building block), `tab` is the KEY (§10.1).
          value={TAB_INDEX[tab]}
          onChange={(i) => set('tab', STATUSES[i])}
        >
          {/* Das Daten-Panel trägt Z1 (Suche · Zähler), Z3 (aktive Filter) und
              die Karten. Die Leiste stempelt `liste-z1`/`-z2`; Z3 kommt aus
              `ActiveFilterBar` INNERHALB der Leiste. */}
          <Panel pad="none" clip data-testid="liste-panel" sx={{ mt: SP.section }}>
            <Box sx={{ px: SP.card, pt: SP.card }}>
              {/* §2.2 Punkt 7, erste Aussage: der Grund der Sperre, sichtbar
                  über der Leiste (N8.5). */}
              {ersteAufbauGesperrt && (
                <Alert
                  severity="warning"
                  sx={{ mb: SP.card }}
                  data-testid="erster-aufbau-gesperrt"
                >
                  Search is locked until the list loads. Use Try again below.
                </Alert>
              )}

              <ListToolbar
                shortcut="/"
                search={{
                  value: suche,
                  // 🔴 PUFFERMODUS: one writer per query (N7.7) — the text is
                  // handed over after 300 ms of rest, Enter at once.
                  onCommit: (v) => set('q', v),
                  placeholder: 'Search competitor, SKU, URL…',
                  label: 'Search competitor, SKU, URL',
                  inputRole: 'searchbox',
                  disabled: ersteAufbauGesperrt,
                }}
                // §2.2 Punkt 7: no counter while the FIRST build failed — the
                // empty state speaks (N4.5/P28). A failed refresh keeps it: the
                // rows (and their number) are still true. 🔴 R15-B1: dagegen
                // steht er NICHT, wenn gar keine eigenen Zeilen da sind (Fehler
                // des Erstaufbaus ODER ein gescheiterter Reiterwechsel).
                count={(ersteAufbauGesperrt || fehlerOhneZeilen) ? undefined : zaehler}
                query={{ chips: query.chips, clearAll: query.clearAll }}
              >
                {/* §2.2 Punkt 7, zweite Aussage: a failed REFRESH with rows on
                    screen keeps the rows and says so above them.
                    🔴 R16-B2: die Ursache des Servers wird MITGERENDERT — bis
                    hierher stand nur der feste Satz, und die gespeicherte
                    `error.message` erreichte den Schirm nirgends. */}
                {refetchFehler && (
                  <Alert
                    severity="error"
                    sx={{ mt: SP.card }}
                    action={
                      <Button size="small" color="inherit" onClick={load}>
                        Retry
                      </Button>
                    }
                  >
                    The list could not be reloaded. The cards below are the last set that loaded.{' '}
                    <Box component="span" data-testid="lade-fehler-ursache">
                      {error.message}
                    </Box>
                  </Alert>
                )}

                {/* 🔴 R16-B2: ein gescheitertes Adopt/Retire/Later nennt die
                    Ursache des Servers und behauptet NICHT, die Liste sei nicht
                    nachgeladen worden. Es sperrt auch nicht mehr alle Karten —
                    gesperrt ist nur die laufende Aktion (`busyId`). */}
                {aktionsFehler && (
                  <Alert severity="error" sx={{ mt: SP.card }} data-testid="aktions-fehler">
                    <Box component="span" data-testid="aktions-fehler-ursache">
                      {aktionsFehler.message}
                    </Box>
                  </Alert>
                )}

                {/* §2.2 Punkt 6 / P13: an UNKNOWN value (`?tab=foo`) is dropped
                    AND reported once — silent ignoring is the defect. */}
                {query.ignoredValues.length > 0 && (
                  <Alert severity="info" data-testid="ignoriert-hinweis" sx={{ mt: SP.card }}>
                    {`Ignored unknown filter value “${query.ignoredValues[0].value}”.`}
                  </Alert>
                )}

                {/* §2.2 Punkt 8 / P18: no view is sortable, so the ORDER is
                    stated in words under the toolbar. */}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  data-testid="sortiert-nach"
                  sx={{ display: 'block', mt: SP.card }}
                >
                  Sorted by newest first.
                </Typography>

                {/* 🔴 R15-B1: solange die Antwort zu einem ANDEREN Reiter gehört
                    (Erstaufbau ODER laufender Wechsel), steht das Skelett — nie
                    die Karten des vorigen Reiters. Ein gescheiterter Wechsel
                    setzt `error` und fällt damit in den Fehler-Leerzustand. */}
                {!error && dataTab !== tab ? (
                  // Erstaufbau: Skelett statt Spinner. Die Lade-Marke
                  // `liste-skelett` ist die Mess-Marke, auf deren Verschwinden
                  // `oeffne` wartet (G5a-NB2).
                  <Stack spacing={SP.card} data-testid="liste-skelett" sx={{ mt: SP.card }}>
                    {[0, 1, 2].map((i) => (
                      <Skeleton key={i} variant="rounded" height={140} />
                    ))}
                  </Stack>
                ) : leerGrund ? (
                  <Box sx={{ mt: SP.card }}>
                    {leerGrund === 'no-data' && (
                      <EmptyState
                        reason="no-data"
                        message="No redirect reviews yet."
                        detail="New cases appear automatically as soon as a scraper target's URL redirects to a page that still shows a price."
                      />
                    )}
                    {leerGrund === 'unknown' && (
                      <EmptyState
                        reason="unknown"
                        message="This view could not be counted."
                        detail="The list loaded, but the counts did not — this view cannot say whether cases exist in another state."
                        onRetry={load}
                      />
                    )}
                    {leerGrund === 'no-match' &&
                      (suche ? (
                        <EmptyState
                          reason="no-match"
                          query={suche}
                          noun="redirect review"
                          onClearSearch={() => set('q', '')}
                        />
                      ) : (
                        <EmptyState
                          reason="no-match"
                          message="No cases in this status."
                          detail="New cases appear automatically as soon as a scraper target's URL redirects to a page that still shows a price."
                        />
                      ))}
                  </Box>
                ) : fehlerOhneZeilen ? (
                  <Box sx={{ mt: SP.card }}>
                    <EmptyState
                      reason="error"
                      message={error.message}
                      detail={error.detail}
                      onRetry={load}
                    />
                  </Box>
                ) : (
                  <Stack spacing={SP.card} data-testid="liste-karte" sx={{ mt: SP.card, pb: SP.card }}>
                    {karten.map((c) => (
                      <Karte
                        key={c.id}
                        c={c}
                        gesperrt={aktionenGesperrt || busyId === c.id}
                        onAct={act}
                      />
                    ))}
                  </Stack>
                )}
              </ListToolbar>
            </Box>
          </Panel>
        </UnderlineTabs>
      </Box>
    </PageContainer>
  );
}
